import { useState } from 'react';
import numeral from 'numeral';

// Constellation
import {
  AdornedInput,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';

import { useGetUser } from '../../hooks/queries/user';
import { useGetAddress } from '../../hooks/queries/address';
import { useGetFilter } from '../../hooks/queries/filter';

import { updateFilterAndNavigate } from '../../flux/actions/FilterActions';

import pathsManifest from '../../utils/pathsManifest';
// @ts-ignore
import { augmentError } from '../../utils/errors';

import type AutocompleteAddress from '../../models/AutocompleteAddress';

const NoZestimateModal = ({
  isOpen,
  onClose,
  address,
  validAddressItem,
}: {
  isOpen: boolean;
  onClose?: () => void;
  address: string;
  validAddressItem?: AutocompleteAddress;
}) => {
  const [inputPrice, updateInputPrice] = useState('');
  const [errorMsg, updateErrorMsg] = useState('');

  const { data: user } = useGetUser();
  const { data: queryAddress } = useGetAddress();
  const { data: filter, updateFilter } = useGetFilter();

  const handleCancel = () => {
    updateErrorMsg('');
    onClose?.();
  };

  const handlePriceChange = (e: React.FormEvent<HTMLInputElement>) => {
    updateErrorMsg('');

    // @ts-ignore https://github.com/desktop/desktop/issues/297
    const enteredValue = e.target.value;

    updateInputPrice(enteredValue ? numeral(enteredValue).format('0,0') : '');
  };

  const handleSubmit = async () => {
    if (!queryAddress || !filter) {
      return null;
    }

    const price = numeral(inputPrice).value();
    if (price === null) {
      updateErrorMsg('Please enter an amount');
      return;
    }

    const partialFilterObj = { price };

    await updateFilterAndNavigate(
      `${pathsManifest.PriceMyRentalPageResults}`,
      partialFilterObj,
      user,
      queryAddress,
      updateFilter,
      filter,
      validAddressItem,
    )().catch(
      // eslint-disable-next-line
      (err: any) => {
        updateErrorMsg('There has been an error. Please try again.');
        augmentError(err, {
          root: 'components/NoZestimateModal#handleSubmit',
        });

        // logger.error(formatForLog(err), 'Filter: Error handling filter price change');
      },
    );
  };

  return (
    <Modal
      size={{ base: 'fullScreen', lg: 'sm' }}
      onOpenChange={(open) => !open && handleCancel()}
      open={isOpen}
      header={
        <Heading level={5}>
          Rent Zestimate<sup>®</sup> not found
        </Heading>
      }
      body={
        <Box>
          <Paragraph css={{ marginBottom: '200' }}>
            We&apos;re unable to provide a Rent Zestimate<sup>®</sup> for <strong>{`${address}`}</strong>.
          </Paragraph>

          <Paragraph css={{ marginBottom: '400' }}>
            Enter a custom price and we&apos;ll show you nearby rental listings closest to the price you choose.
          </Paragraph>

          <FormField
            label={<Label>Custom price</Label>}
            required
            error={errorMsg !== ''}
            control={
              <AdornedInput
                input={
                  <AdornedInput.Input onChange={handlePriceChange} placeholder="Enter amount" value={inputPrice} />
                }
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                size="lg"
                css={{ marginTop: '200' }}
              />
            }
            description={errorMsg !== '' ? <FormHelp css={{ paddingTop: '200' }}>{errorMsg}</FormHelp> : undefined}
          />

          <Box css={{ marginTop: '200', marginBottom: '400' }} />
        </Box>
      }
      footer={
        <ButtonGroup aria-label="See listings">
          <TextButton onClick={handleCancel}>Cancel</TextButton>
          <Button tone="brand" emphasis="filled" onClick={handleSubmit}>
            See listings
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default NoZestimateModal;
